import { useState } from "react";
import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { BarChart3, Users, Server, Smile, Megaphone } from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { USERS, SERVERS, getInitials, getAvatarColor } from "@/mock/data";
import type { User } from "@snowcord/shared";
import "./AdminLayout.css";

const adminUsers: User[] = USERS.map((u) => ({
  ...u,
  passwordHash: "",
  isBanned: u.id === "u3",
}));

function AdminRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  if (!user?.isAdmin) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

function NavItem({ icon, label, path }: { icon: React.ReactNode; label: string; path: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = location.pathname === path;

  return (
    <button
      className={`admin-layout__nav-item${isActive ? " admin-layout__nav-item--active" : ""}`}
      onClick={() => navigate(path)}
    >
      {icon}
      {label}
    </button>
  );
}

function OverviewPage() {
  return (
    <>
      <h1 className="admin-page-title">Overview</h1>
      <div className="admin-overview">
        <div className="admin-overview__card">
          <div className="admin-overview__card-value">156</div>
          <div className="admin-overview__card-label">Total Users</div>
        </div>
        <div className="admin-overview__card">
          <div className="admin-overview__card-value">94</div>
          <div className="admin-overview__card-label">Active Today</div>
        </div>
        <div className="admin-overview__card">
          <div className="admin-overview__card-value">847</div>
          <div className="admin-overview__card-label">Messages Today</div>
        </div>
      </div>
    </>
  );
}

function UsersPage() {
  const [search, setSearch] = useState("");

  const filteredUsers = adminUsers.filter(
    (u) =>
      u.username.toLowerCase().includes(search.toLowerCase()) ||
      u.displayName.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <>
      <h1 className="admin-page-title">Users</h1>
      <input
        className="admin-search"
        placeholder="Search users..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <table className="admin-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Username</th>
            <th>Joined</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.map((user) => (
            <tr key={user.id}>
              <td>
                <span
                  className="admin-table__avatar"
                  style={{ background: getAvatarColor(user.id) }}
                >
                  {getInitials(user.displayName)}
                </span>
                {user.displayName}
              </td>
              <td>@{user.username}</td>
              <td>{new Date(user.createdAt).toLocaleDateString()}</td>
              <td>
                {user.isBanned ? (
                  <span className="admin-table__badge admin-table__badge--banned">Banned</span>
                ) : user.isAdmin ? (
                  <span className="admin-table__badge admin-table__badge--admin">Admin</span>
                ) : (
                  <span className="admin-table__badge admin-table__badge--active">Active</span>
                )}
              </td>
              <td>
                {user.isBanned ? (
                  <button className="admin-table__btn admin-table__btn--unban">Unban</button>
                ) : (
                  <button className="admin-table__btn admin-table__btn--ban">Ban</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function ServersPage() {
  return (
    <>
      <h1 className="admin-page-title">Servers</h1>
      <table className="admin-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Owner</th>
            <th>Members</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {SERVERS.map((server) => (
            <tr key={server.id}>
              <td>{server.name}</td>
              <td>Owner</td>
              <td>{server.memberCount}</td>
              <td>{new Date(server.createdAt).toLocaleDateString()}</td>
              <td>
                <button className="admin-table__btn admin-table__btn--delete">Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function EmojisPage() {
  const [emojiName, setEmojiName] = useState("");
  const mockEmojis = [
    { id: "e1", name: "wave", emoji: "👋" },
    { id: "e2", name: "fire", emoji: "🔥" },
    { id: "e3", name: "rocket", emoji: "🚀" },
    { id: "e4", name: "check", emoji: "✅" },
    { id: "e5", name: "brain", emoji: "🧠" },
    { id: "e6", name: "star", emoji: "⭐" },
  ];

  return (
    <>
      <h1 className="admin-page-title">Emojis</h1>
      <div className="admin-upload-row">
        <input
          className="admin-upload-input"
          placeholder="Emoji name"
          value={emojiName}
          onChange={(e) => setEmojiName(e.target.value)}
        />
        <button className="admin-upload-btn">Upload</button>
      </div>
      <div className="admin-emoji-grid">
        {mockEmojis.map((e) => (
          <div className="admin-emoji-item" key={e.id}>
            <div className="admin-emoji-item__img">{e.emoji}</div>
            <div className="admin-emoji-item__name">:{e.name}:</div>
            <button className="admin-emoji-item__delete" aria-label="Delete emoji">x</button>
          </div>
        ))}
      </div>
    </>
  );
}

function AnnouncePage() {
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);

  function handleSend() {
    if (!message.trim()) return;
    setSent(true);
    setMessage("");
    setTimeout(() => setSent(false), 3000);
  }

  return (
    <>
      <h1 className="admin-page-title">Announcement</h1>
      <div className="admin-announce">
        <textarea
          className="admin-announce__textarea"
          placeholder="Write a platform-wide announcement..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        <button className="admin-announce__send" onClick={handleSend}>
          {sent ? "Sent!" : "Send Announcement"}
        </button>
      </div>
    </>
  );
}

export function AdminLayout() {
  return (
    <AdminRoute>
      <div className="admin-layout">
        <nav className="admin-layout__sidebar">
          <div className="admin-layout__title">Admin Panel</div>
          <NavItem icon={<BarChart3 size={16} />} label="Overview" path="/admin" />
          <NavItem icon={<Users size={16} />} label="Users" path="/admin/users" />
          <NavItem icon={<Server size={16} />} label="Servers" path="/admin/servers" />
          <NavItem icon={<Smile size={16} />} label="Emojis" path="/admin/emojis" />
          <NavItem icon={<Megaphone size={16} />} label="Announce" path="/admin/announce" />
        </nav>
        <main className="admin-layout__main">
          <Routes>
            <Route index element={<OverviewPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="servers" element={<ServersPage />} />
            <Route path="emojis" element={<EmojisPage />} />
            <Route path="announce" element={<AnnouncePage />} />
          </Routes>
        </main>
      </div>
    </AdminRoute>
  );
}
